import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, TrendingUp, Film } from 'lucide-react';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Navigation } from '../components/Navigation';
import { ThemeToggle } from '../components/ThemeToggle';

interface Genre {
    genre: string;
    series_count: number;
    recap_count: number;
    updated_at: string;
}

const GenresPage: React.FC = () => {
    const [genres, setGenres] = useState<Genre[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sortBy, setSortBy] = useState<'series_count' | 'recap_count' | 'genre'>('series_count');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

    useEffect(() => {
        fetchGenres();
    }, [sortBy, sortOrder]);

    const fetchGenres = async () => {
        try {
            setLoading(true);
            setError(null);

            const url = `/api/genres?sortBy=${sortBy}&sortOrder=${sortOrder}`;
            console.log('[GenresPage] Fetching genres from:', url);

            const response = await fetch(url, {
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            console.log('[GenresPage] Response status:', response.status, response.statusText);

            // Parse response once
            let data;
            try {
                const text = await response.text();
                console.log('[GenresPage] Response text:', text.substring(0, 200));
                data = JSON.parse(text);
            } catch (parseError) {
                console.error('[GenresPage] Failed to parse JSON response:', parseError);
                throw new Error(`Server returned invalid JSON (${response.status} ${response.statusText}). Check server logs.`);
            }

            if (!response.ok) {
                const errorMessage = data?.error || data?.message || `HTTP ${response.status}: Failed to fetch genres`;
                console.error('[GenresPage] API error:', {
                    status: response.status,
                    statusText: response.statusText,
                    error: errorMessage,
                    data: data,
                    url: response.url
                });
                throw new Error(errorMessage);
            }

            if (!data.success) {
                throw new Error(data.error || 'Failed to fetch genres');
            }

            console.log('[GenresPage] Successfully loaded', data.data?.length || 0, 'genres');
            setGenres(data.data || []);
        } catch (err) {
            let errorMessage = 'An error occurred while fetching genres';

            if (err instanceof TypeError && err.message.includes('fetch')) {
                errorMessage = 'Network error: Unable to connect to server. Please check your connection and try again.';
            } else if (err instanceof Error) {
                errorMessage = err.message;
            }

            console.error('[GenresPage] Error fetching genres:', err);
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (newSortBy: 'series_count' | 'recap_count' | 'genre') => {
        if (sortBy === newSortBy) {
            // Toggle sort order if clicking the same column
            setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortBy(newSortBy);
            setSortOrder('DESC'); // Default to DESC for counts, ASC for genre name
        }
    };

    const formatNumber = (num: number) => {
        return new Intl.NumberFormat('en-US').format(num);
    };

    return (
        <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white min-h-screen font-sans transition-colors duration-200">
            {/* Header */}
            <header className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40 transition-colors duration-200">
                <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-2 md:space-x-4 lg:space-x-6">
                            <Link to="/" className="flex items-center space-x-1 md:space-x-2">
                                <div className="p-1.5 md:p-2 bg-purple-600 rounded-lg">
                                    <BookOpen className="w-5 h-5 md:w-6 md:h-6 text-white" />
                                </div>
                                <span className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white">ManhwaRecap</span>
                            </Link>
                            <Navigation />
                        </div>
                        <ThemeToggle />
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                        Browse by Genre
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Explore manhwa recaps organized by genre
                    </p>
                </div>

                {/* Sort Controls */}
                <div className="mb-6 flex flex-wrap items-center gap-4">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sort by:</span>
                    <button
                        onClick={() => handleSort('series_count')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${sortBy === 'series_count'
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                            }`}
                    >
                        Series Count {sortBy === 'series_count' && (sortOrder === 'DESC' ? '↓' : '↑')}
                    </button>
                    <button
                        onClick={() => handleSort('recap_count')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${sortBy === 'recap_count'
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                            }`}
                    >
                        Recap Count {sortBy === 'recap_count' && (sortOrder === 'DESC' ? '↓' : '↑')}
                    </button>
                    <button
                        onClick={() => handleSort('genre')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${sortBy === 'genre'
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                            }`}
                    >
                        Genre Name {sortBy === 'genre' && (sortOrder === 'ASC' ? '↑' : '↓')}
                    </button>
                </div>

                {/* Error State */}
                {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                        <p className="text-red-800 dark:text-red-200">{error}</p>
                    </div>
                )}

                {/* Loading State */}
                {loading ? (
                    <LoadingSpinner />
                ) : (
                    /* Genres Grid */
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {genres.map((genre) => (
                            <Link
                                key={genre.genre}
                                to={`/?genre=${encodeURIComponent(genre.genre)}`}
                                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-lg hover:border-purple-500 dark:hover:border-purple-600 transition-all duration-200 group"
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                                        {genre.genre}
                                    </h3>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                        <BookOpen className="w-4 h-4 mr-2" />
                                        <span className="font-medium">{formatNumber(genre.series_count)}</span>
                                        <span className="ml-1">series</span>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                        <Film className="w-4 h-4 mr-2" />
                                        <span className="font-medium">{formatNumber(genre.recap_count)}</span>
                                        <span className="ml-1">recaps</span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}

                {/* Empty State */}
                {!loading && !error && genres.length === 0 && (
                    <div className="text-center py-12">
                        <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">No genres found</h3>
                        <p className="text-gray-500">Genres will appear here once they are populated.</p>
                    </div>
                )}
            </main>
        </div>
    );
};

export default GenresPage;

